import { Navigate } from 'react-router-dom';
import { AppLoader } from '../../components/loading/AppLoader.js';
import { ConnectionError } from '../../components/loading/ConnectionError.js';
import { useEnglishLevel } from '../leveling/useEnglishLevel.js';
import { HomeScreen } from './HomeScreen.js';
import { A0Screen } from '../a0/A0Screen.js';
import { A0_LEGACY_APP_ENABLED } from '../a0/legacy.js';

/**
 * Dispatcher de `/home`: el motor A0 (principiante absoluto, plan 32) NO tiene
 * ruta propia — vive bajo `/home` y se elige por nivel. Así la URL es siempre
 * `/home` para todos (sin exponer la jerga `A0` en la barra de direcciones,
 * coherente con plan 31 que la suavizó a "Primeros pasos"), y la graduación
 * A0→A1 cambia lo que se renderiza SIN cambiar la URL.
 *
 * Es el ÚNICO gate de nivel de `/home`: resuelve `/state` una vez y decide.
 * - En carga fría (sin caché) muestra el loader; en revisita, la caché de sesión
 *   de `useEnglishLevel` ya trae el nivel → decide al instante, sin flash.
 * - Los hijos heredan el dato vía esa misma caché (montan sin segundo loader).
 * - El rebote inverso de las superficies A1+ (Biblioteca/Actividad/Progreso)
 *   vive en `A0Gated`, que ahora rebota acá (`/home`), no a una ruta `/a0`.
 *
 * Graduación: como invalidar la caché NO notifica a los hooks montados, A0Screen
 * no puede forzar el switch por su cuenta sin un cambio de ruta. Por eso recibe
 * `onGraduated` = nuestro `refresh`: al "Entrar" desde el reveal, re-leemos
 * `/state` (ya A1) y este dispatcher re-renderiza el Home completo en el lugar.
 */
export function HomeOrA0() {
  const { state, error, loading, refresh } = useEnglishLevel();

  // Carga fría fallida sin caché: `/state` no respondió y no hay dato previo.
  if (!state && error && !loading) {
    return <ConnectionError onRetry={() => void refresh()} />;
  }
  if (!state) {
    return <AppLoader />;
  }
  if (!state.onboarded) {
    // El onboarding arranca por los datos (/welcome: nombre·país·edad →
    // motivación → nivel). Antes esto iba directo a `/onboarding/level`, lo que
    // salteaba esos pasos para TODO usuario no-nuevo (cuenta preexistente,
    // abandonada o reseteada): el país sólo se veía en el camino `isNewUser`.
    // Ahora gatea por `!onboarded`, no por "primer login". Excepción: si quedó a
    // mitad del placement quiz (`pendingQuiz`), lo depositamos en el quiz, que
    // resume solo, sin re-pedir los datos.
    return <Navigate to={state.pendingQuiz ? '/onboarding/level' : '/welcome'} replace />;
  }
  // A0 → motor de reconocimiento; el resto → Home normal. Con la app A0
  // dormida (plan 48) el A0 también va al Home normal.
  if (A0_LEGACY_APP_ENABLED && state.cefrGlobal === 'A0') {
    return <A0Screen onGraduated={() => void refresh()} />;
  }
  return <HomeScreen />;
}
